import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiEye, FiEyeOff, FiAlertCircle, FiClock, FiUser } from 'react-icons/fi';
import authService, { AuthError, DuplicateVisitError } from '../services/authService';

// ===== ANIMATIONS =====
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

// ===== STYLED COMPONENTS =====
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #FFFFFF;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0;
  text-align: center;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #F3F4F6;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6B7280;
  padding: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #E5E7EB;
    color: #374151;
  }
  
  &:focus {
    outline: 2px solid #3B82F6;
    outline-offset: 2px;
  }
`;

const WelcomeBackIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4F46E5;
  
  svg {
    width: 28px;
    height: 28px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #222222;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 0.9375rem;
  color: #666666;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ModalBody = styled.div`
  padding: 0 24px 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #333333;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${props => props.hasError ? '#DC2626' : '#E5E7EB'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#DC2626' : '#3B82F6'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
  
  &:disabled {
    background: #F9FAFB;
    color: #6B7280;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #374151;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorMessage = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 12px 16px;
  color: #DC2626;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  
  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }
`;

const DuplicateBlockMessage = styled.div`
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 16px;
  color: #92400E;
  font-size: 0.875rem;
  line-height: 1.5;
  
  .title {
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  .days {
    font-weight: 700;
    font-size: 1.25rem;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #222222;
  color: #FFFFFF;
  border: none;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background: #000000;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 8px 0;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #E5E7EB;
  }
  
  span {
    color: #9CA3AF;
    font-size: 0.875rem;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: transparent;
  color: #4B5563;
  border: 1px solid #E5E7EB;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #F9FAFB;
    border-color: #D1D5DB;
  }
`;

const ForgotPasswordLink = styled.a`
  color: #3B82F6;
  font-size: 0.875rem;
  text-decoration: none;
  text-align: center;
  display: block;
  margin-top: 8px;
  
  &:hover {
    text-decoration: underline;
  }
`;

// ===== MAIN COMPONENT =====
const ExistingPatientLoginModal = ({
  templateId,
  onSuccess,
  onClose,
  onContinueAsNew,
  initialEmail = '',
  portalUrl = '',
  login = authService.login.bind(authService),
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateBlock, setDuplicateBlock] = useState(null);

  const passwordInputRef = useRef(null);

  // Focus password input when modal opens (email is pre-filled)
  useEffect(() => {
    if (initialEmail && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [initialEmail]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setDuplicateBlock(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(email, password, templateId);

      // Call success callback with auth data
      await onSuccess({
        authenticated: true,
        sessionId: result.sessionId,
        accessToken: result.accessToken,
        prefillData: result.prefillData,
        patient: result.patient,
        template: result.template,
      });
    } catch (err) {
      if (err instanceof DuplicateVisitError) {
        setDuplicateBlock({
          message: err.message,
          daysRemaining: err.daysRemaining,
          blockedUntil: err.blockedUntil,
        });
      } else if (err instanceof AuthError) {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
        console.error('Login error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Continue as New Patient"
  const handleContinueAsNew = () => {
    if (onContinueAsNew) {
      onContinueAsNew();
    }
    onClose();
  };

  // Forgot password link
  const forgotPasswordUrl = portalUrl
    ? `${portalUrl}/auth/forgot-password`
    : '/auth/forgot-password';

  return (
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose} aria-label="Close login modal" type="button">
            ×
          </CloseButton>
          <WelcomeBackIcon aria-hidden="true">
            <FiUser />
          </WelcomeBackIcon>
          <ModalTitle id="login-modal-title">Welcome Back!</ModalTitle>
          <ModalSubtitle>
            Sign in to continue with your saved information
          </ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          <Form onSubmit={handleSubmit}>
            {/* Email Input */}
            <FormGroup>
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isLoading || !!initialEmail}
                hasError={!!error}
                autoComplete="email"
              />
            </FormGroup>

            {/* Password Input */}
            <FormGroup>
              <Label htmlFor="login-password">Password</Label>
              <InputWrapper>
                <Input
                  id="login-password"
                  ref={passwordInputRef}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  hasError={!!error}
                  autoComplete="current-password"
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage>
                <FiAlertCircle />
                <span>{error}</span>
              </ErrorMessage>
            )}

            {duplicateBlock && (
              <DuplicateBlockMessage>
                <div className="title">
                  <FiClock />
                  Too Soon to Reorder
                </div>
                <p>{duplicateBlock.message}</p>
                {duplicateBlock.daysRemaining > 0 && (
                  <p>
                    You can start again in <span className="days">{duplicateBlock.daysRemaining}</span> days.
                  </p>
                )}
              </DuplicateBlockMessage>
            )}

            {/* Submit Button */}
            <SubmitButton type="submit" disabled={isLoading || !!duplicateBlock}>
              {isLoading ? (
                <>
                  <LoadingSpinner />
                  Signing in...
                </>
              ) : duplicateBlock ? (
                'Come Back Later'
              ) : (
                'Sign In & Continue'
              )}
            </SubmitButton>

            {/* Forgot Password Link */}
            <ForgotPasswordLink href={forgotPasswordUrl} target="_blank" rel="noopener noreferrer">
              Forgot your password?
            </ForgotPasswordLink>

            {/* Divider */}
            {!duplicateBlock && (
              <>
                <Divider><span>or</span></Divider>

                {/* Continue as New Patient */}
                <SecondaryButton type="button" onClick={handleContinueAsNew}>
                  Continue as New Patient
                </SecondaryButton>
              </>
            )}
          </Form>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default ExistingPatientLoginModal;
